import React, { useRef, useEffect, useState, useCallback } from "react";
import Hls from "hls.js";

/**
 * HLS video player with quality selector and MP4 fallback.
 *
 * Props:
 *  - src        : m3u8 URL (primary)
 *  - fallbackSrc: mp4 URL  (used when HLS not supported or src missing)
 *  - poster     : poster / thumbnail image
 *  - style      : pass-through style for the container
 *  - autoPlay   : boolean
 */
const VideoPlayer = ({ src, fallbackSrc, poster, style, autoPlay = true }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [levels, setLevels] = useState([]);          // available HLS quality levels
  const [currentLevel, setCurrentLevel] = useState(-1); // -1 = Auto
  const [showMenu, setShowMenu] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  const isM3u8Url = useCallback((value) => {
    if (!value || typeof value !== "string") return false;
    return /\.m3u8($|\?)/i.test(value) || value.includes("master.m3u8") || value.includes("/hls/");
  }, []);

  const toMasterM3u8Url = useCallback((value) => {
    if (!value || typeof value !== "string") return null;
    try {
      const parsed = new URL(value, window.location.origin);
      const path = parsed.pathname || "";
      const masterPath = path.replace(/\/[^/]*$/, "/master.m3u8");
      parsed.pathname = masterPath;
      return parsed.toString();
    } catch {
      const clean = value.split("?")[0];
      if (!clean.includes("/")) return "master.m3u8";
      return `${clean.replace(/\/[^/]*$/, "/master.m3u8")}`;
    }
  }, []);

  const isUrlReachable = useCallback(async (url) => {
    if (!url) return false;
    try {
      const headRes = await fetch(url, { method: "HEAD", cache: "no-store" });
      if (headRes.ok) return true;
    } catch {
      // Some CDNs block HEAD. Fall back to GET below.
    }
    try {
      const getRes = await fetch(url, { method: "GET", cache: "no-store" });
      return getRes.ok;
    } catch {
      return false;
    }
  }, []);

  const destroyHls = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;

    // Reset
    destroyHls();
    setLevels([]);
    setCurrentLevel(-1);
    setUsingFallback(false);
    setShowMenu(false);

    const startPlayback = async () => {
      const mp4 = fallbackSrc || (!isM3u8Url(src) ? src : null);

      // Candidate order:
      // 1) explicit HLS URL (if provided)
      // 2) derived master.m3u8 from src
      // 3) derived master.m3u8 from fallback mp4
      const hlsCandidates = [
        isM3u8Url(src) ? src : null,
        !isM3u8Url(src) ? toMasterM3u8Url(src) : null,
        !isM3u8Url(fallbackSrc) ? toMasterM3u8Url(fallbackSrc) : null,
      ].filter(Boolean);

      const uniqueCandidates = Array.from(new Set(hlsCandidates));
      let chosenHls = null;
      for (const candidate of uniqueCandidates) {
        // If master.m3u8 is reachable, prefer HLS.
        // Otherwise continue and eventually fall back to mp4.
        // eslint-disable-next-line no-await-in-loop
        const ok = await isUrlReachable(candidate);
        if (ok) {
          chosenHls = candidate;
          break;
        }
      }

      if (cancelled) return;

      if (!chosenHls) {
        video.src = mp4 || src || "";
        setUsingFallback(true);
        return;
      }

      // Prefer hls.js over native HLS so quality selection remains available.
      if (Hls.isSupported()) {
        const hls = new Hls({ startLevel: -1 }); // -1 = auto
        hlsRef.current = hls;
        let manifestLoaded = false;

        hls.loadSource(chosenHls);
        hls.attachMedia(video);

        // Timeout: if manifest is reachable but not parsed quickly, fall back to mp4.
        const timeout = setTimeout(() => {
          if (!manifestLoaded && mp4 && !cancelled) {
            destroyHls();
            video.src = mp4;
            setUsingFallback(true);
            setLevels([]);
          }
        }, 5000);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          manifestLoaded = true;
          clearTimeout(timeout);
          setLevels(hls.levels);
          setCurrentLevel(hls.currentLevel);
          if (autoPlay) video.play().catch(() => {});
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
          setCurrentLevel(data.level);
        });

        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal && mp4 && !cancelled) {
            clearTimeout(timeout);
            destroyHls();
            video.src = mp4;
            setUsingFallback(true);
            setLevels([]);
          }
        });
        return;
      }

      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = chosenHls;
        return;
      }

      // No HLS engine available in browser — final fallback.
      video.src = mp4 || src || "";
      setUsingFallback(true);
    };

    startPlayback();

    return () => {
      cancelled = true;
      destroyHls();
    };
  }, [src, fallbackSrc, autoPlay, destroyHls, isM3u8Url, isUrlReachable, toMasterM3u8Url]);

  const handleQualityChange = (levelIndex) => {
    const hls = hlsRef.current;
    if (!hls) return;
    hls.currentLevel = levelIndex; // -1 = auto
    setCurrentLevel(levelIndex);
    setShowMenu(false);
  };

  const qualityLabel = (level) => {
    if (!level) return "";
    return `${level.height}p`;
  };

  const activeLabel =
    currentLevel === -1
      ? "Auto"
      : levels[currentLevel]
      ? qualityLabel(levels[currentLevel])
      : "Auto";

  return (
    <div style={{ position: "relative", display: "inline-block", maxWidth: "100%", maxHeight: "100%", ...style }}>
      <video
        ref={videoRef}
        poster={poster}
        controls
        autoPlay={autoPlay}
        preload="metadata"
        style={{ maxWidth: "85vw", maxHeight: "85vh", objectFit: "contain", borderRadius: 8, display: "block" }}
      />

      {/* Quality selector — show when HLS levels exist and not in fallback mode */}
      {levels.length >= 1 && !usingFallback && (
        <div style={styles.qualityWrapper}>
          <button
            style={styles.qualityBtn}
            onClick={() => setShowMenu((v) => !v)}
            title="Video quality"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span style={{ marginLeft: 4, fontSize: 12 }}>{activeLabel}</span>
          </button>

          {showMenu && (
            <div style={styles.qualityMenu}>
              <div
                style={{
                  ...styles.qualityItem,
                  ...(currentLevel === -1 ? styles.qualityItemActive : {}),
                }}
                onClick={() => handleQualityChange(-1)}
              >
                Auto
              </div>
              {levels.map((level, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.qualityItem,
                    ...(currentLevel === i ? styles.qualityItemActive : {}),
                  }}
                  onClick={() => handleQualityChange(i)}
                >
                  {qualityLabel(level)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fallback indicator */}
      {usingFallback && (
        <div style={styles.fallbackBadge}>MP4</div>
      )}
    </div>
  );
};

const styles = {
  qualityWrapper: {
    position: "absolute",
    bottom: 52,
    right: 12,
    zIndex: 10,
  },
  qualityBtn: {
    display: "flex",
    alignItems: "center",
    background: "rgba(0,0,0,0.6)",
    border: "none",
    color: "#fff",
    padding: "5px 10px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    backdropFilter: "blur(4px)",
  },
  qualityMenu: {
    position: "absolute",
    bottom: "100%",
    right: 0,
    marginBottom: 6,
    background: "rgba(0,0,0,0.85)",
    borderRadius: 8,
    overflow: "hidden",
    minWidth: 100,
    backdropFilter: "blur(8px)",
  },
  qualityItem: {
    padding: "8px 16px",
    color: "#ccc",
    fontSize: 13,
    cursor: "pointer",
    textAlign: "center",
    transition: "background 0.15s",
  },
  qualityItemActive: {
    color: "#fff",
    fontWeight: 700,
    background: "rgba(108,99,255,0.5)",
  },
  fallbackBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    background: "rgba(0,0,0,0.55)",
    color: "#fbbf24",
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: 4,
  },
};

export default VideoPlayer;
